// src/contexts/StreamingContext.tsx
"use client";

import { createContext, useContext, ReactNode, useReducer, useCallback, useRef, useMemo, useEffect } from 'react';
import { User } from 'firebase/auth';
import type { ChatMessage as LocalChatMessage, FirstImpressionTool } from '@/types';
import { 
  messageReducer, 
  initialMessageState, 
  shouldClearStreamingOnNewMessage,
  getEffectiveThinkingContent
} from '@/utils/messageStateManager';
import { StreamingService, type StreamingServiceConfig } from '@/services/streamingService';
import { EnhancedFirebaseService } from '@/services/firebaseService';
import { SessionManager } from '@/utils/sessionManager';
import { useToast } from '@/components/hooks/use-toast';
import { useMessageState } from './MessageStateContext';
import { useArtifacts } from './ArtifactContext';
import type { ArtifactData } from '@/types';

interface StreamingContextType {
  // State
  streamingMessage: LocalChatMessage | null;
  effectiveThinkingContent: string;
  isStreaming: boolean;
  error: string | null;
  sessionId: string;
  traceId: string | null;
  
  // Actions
  sendMessage: (text: string) => Promise<void>;
  sendToolConfirmationMessage: (
    userMessage: string, 
    confirmedTools: FirstImpressionTool[], 
    traceId: string,
    signal: AbortSignal
  ) => Promise<void>;
  stopStreaming: () => void;
  clearError: () => void;
}

const StreamingContext = createContext<StreamingContextType | null>(null);

export const useStreaming = () => {
  const context = useContext(StreamingContext);
  if (!context) {
    throw new Error('useStreaming must be used within StreamingProvider');
  }
  return context;
};

interface StreamingProviderProps {
  children: ReactNode;
  assistantConfig: StreamingServiceConfig;
  currentUser: User | null;
}

export const StreamingProvider: React.FC<StreamingProviderProps> = ({
  children,
  assistantConfig,
  currentUser
}) => {
  const [state, dispatch] = useReducer(messageReducer, initialMessageState);
  const { toast } = useToast();
  const { messages, selectedItems, messagesLoaded } = useMessageState();
  const { getArtifactsForApi, getSelectiveArtifactsForApi, addArtifact } = useArtifacts();
  
  // Session management
  const sessionManagerRef = useRef(new SessionManager());
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Check if we should clear streaming message when new Firestore messages arrive
  useEffect(() => {
    if (shouldClearStreamingOnNewMessage({ 
      ...state, 
      firestoreMessages: messages 
    })) {
      dispatch({ type: 'CLEAR_STREAMING_MESSAGE' });
    }
  }, [messages, state]);
  
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || !currentUser?.email || !assistantConfig.assistantId) {
      return;
    }
    
    // Only process if not already streaming
    if (state.isStreaming) {
      return;
    }
    
    const sessionManager = sessionManagerRef.current;
    
    try {
      // Create trace ID
      const traceId = sessionManager.createNewTraceId();
      
      // Create message payload and save to Firestore
      const messageData = EnhancedFirebaseService.createUserMessagePayload(text, currentUser, traceId);
      const savePromise = EnhancedFirebaseService.saveMessage(assistantConfig.assistantId, messageData);
      
      // Clean up any existing controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      // Create streaming message
      const streamingMsg: LocalChatMessage = {
        sender: 'bot',
        content: '',
        timestamp: Date.now(),
        userName: assistantConfig.name,
        photoURL: assistantConfig.avatar,
        traceId
      };
      
      dispatch({ type: 'START_STREAMING', message: streamingMsg });
      
      // Get current artifacts for API inclusion - using selective mode
      // Default to 'recent' mode with last 2 artifacts for better performance
      const artifactContent = getSelectiveArtifactsForApi({
        mode: 'recent',
        limit: 2,
        assistantId: assistantConfig.assistantId,
        tools: assistantConfig.tools
      });
      
      // Call streaming service
      await StreamingService.sendMessage(
        text,
        assistantConfig,
        {
          currentUser,
          firestoreMessages: messages,
          sessionId: sessionManager.getSessionId(),
          selectedItems,
          artifactContent: artifactContent || undefined,
          signal
        },
        {
          onStreamStart: () => {
            // Already handled by START_STREAMING dispatch
          },
          onStreamChunk: (chunk) => {
            dispatch({ 
              type: 'UPDATE_STREAMING_CONTENT', 
              chunk
            });
          },
          onThinkingContent: (content) => {
            dispatch({ type: 'UPDATE_THINKING_CONTENT', content });
          },
          onArtifactContent: (artifacts: ArtifactData[]) => {
            // Handle artifact content during regular streaming
            console.log('Received artifacts during streaming:', artifacts);
            artifacts.forEach(artifact => addArtifact(artifact));
          },
          onStreamEnd: (finalContent, thinkingContent) => {
            dispatch({ 
              type: 'END_STREAMING', 
              finalContent, 
              thinkingContent 
            });
          },
          onStreamError: (err) => {
            dispatch({ 
              type: 'STREAMING_ERROR', 
              error: err instanceof Error ? err.message : 'Streaming error' 
            });
          }
        }
      );
      
      // Wait for Firestore save and update usage stats
      await savePromise;
      await EnhancedFirebaseService.updateUsageStats(assistantConfig.assistantId);
        
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    }
  }, [state.isStreaming, currentUser, assistantConfig, messages, selectedItems, toast, getSelectiveArtifactsForApi, addArtifact]);
  
  const sendToolConfirmationMessage = useCallback(async (
    userMessage: string, 
    confirmedTools: FirstImpressionTool[], 
    traceId: string,
    signal: AbortSignal
  ) => {
    if (!currentUser?.email || !assistantConfig.assistantId) {
      return;
    }
    
    // Only process if not already streaming
    if (state.isStreaming) {
      return;
    }
    
    const sessionManager = sessionManagerRef.current;
    
    try {
      // Clean up any existing controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Store the provided controller
      abortControllerRef.current = new AbortController();
      
      // Create streaming message
      const streamingMsg: LocalChatMessage = {
        sender: 'bot',
        content: '',
        timestamp: Date.now(),
        userName: assistantConfig.name,
        photoURL: assistantConfig.avatar,
        traceId
      };
      
      dispatch({ type: 'START_STREAMING', message: streamingMsg });
      
      // Get current artifacts for API inclusion - using selective mode
      // For tool confirmations, include active artifact to maintain context
      const artifactContent = getSelectiveArtifactsForApi({
        mode: 'active',
        assistantId: assistantConfig.assistantId,
        tools: assistantConfig.tools
      });
      
      // Call streaming service with tool confirmation
      await StreamingService.sendToolConfirmationMessage(
        userMessage,
        confirmedTools,
        assistantConfig,
        {
          currentUser,
          firestoreMessages: messages,
          sessionId: sessionManager.getSessionId(),
          selectedItems,
          traceId,
          artifactContent: artifactContent || undefined,
          signal
        },
        {
          onStreamStart: () => {
            // Already handled by START_STREAMING dispatch
          },
          onStreamChunk: (chunk) => {
            dispatch({ 
              type: 'UPDATE_STREAMING_CONTENT', 
              chunk
            });
          },
          onThinkingContent: (content) => {
            dispatch({ type: 'UPDATE_THINKING_CONTENT', content });
          },
          onArtifactContent: (artifacts: ArtifactData[]) => {
            // Handle artifact content during tool confirmation
            console.log('Received artifacts during tool confirmation:', artifacts);
            artifacts.forEach(artifact => addArtifact(artifact));
          },
          onStreamEnd: (finalContent, thinkingContent) => {
            dispatch({ 
              type: 'END_STREAMING', 
              finalContent, 
              thinkingContent 
            });
          },
          onStreamError: (err) => {
            console.error('Streaming error:', err);
            dispatch({ 
              type: 'STREAMING_ERROR', 
              error: err instanceof Error ? err.message : String(err)
            });
          }
        }
      );
      
      // Update usage stats
      await EnhancedFirebaseService.updateUsageStats(assistantConfig.assistantId);
        
    } catch (error) {
      console.error('Error sending tool confirmation message:', error);
      dispatch({ 
        type: 'STREAMING_ERROR', 
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }, [state.isStreaming, currentUser, assistantConfig, messages, selectedItems, getSelectiveArtifactsForApi, addArtifact]);
  
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current && state.isStreaming) {
      // Preserve thinking content before stopping
      dispatch({ 
        type: 'PRESERVE_THINKING_CONTENT', 
        content: state.streamingThinkingContent 
      });
      
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      
      dispatch({ 
        type: 'STREAMING_ERROR', 
        error: 'Streaming stopped by user' 
      });
    }
  }, [state.isStreaming, state.streamingThinkingContent]);
  
  const clearError = useCallback(() => {
    dispatch({ type: 'STREAMING_ERROR', error: '' });
  }, []);
  
  // Auto-send "Introduce yourself" message when no messages exist
  useEffect(() => {
    // Only send introduction if:
    // 1. Messages have been loaded from Firestore (to avoid race condition)
    // 2. There are no messages
    // 3. User is logged in
    // 4. Not currently streaming
    // 5. Assistant ID exists
    if (
      messagesLoaded && // IMPORTANT: Wait for Firestore to respond first
      messages.length === 0 && 
      currentUser?.email && 
      !state.isStreaming && 
      assistantConfig.assistantId
    ) {
      const timer = setTimeout(() => {
        sendMessage("Hello! Please introduce yourself and tell me what you can help with.");
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [messagesLoaded, messages.length, currentUser?.email, state.isStreaming, assistantConfig.assistantId, sendMessage]);
  
  const sessionManager = sessionManagerRef.current;
  
  const contextValue = useMemo((): StreamingContextType => ({
    streamingMessage: state.streamingMessage,
    effectiveThinkingContent: getEffectiveThinkingContent(state),
    isStreaming: state.isStreaming,
    error: state.error,
    sessionId: sessionManager.getSessionId(),
    traceId: sessionManager.getTraceId(),
    sendMessage,
    sendToolConfirmationMessage,
    stopStreaming,
    clearError
  }), [
    state,
    sendMessage,
    sendToolConfirmationMessage,
    stopStreaming,
    clearError,
    sessionManager
  ]);
  
  return (
    <StreamingContext.Provider value={contextValue}>
      {children}
    </StreamingContext.Provider>
  );
};